import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type BlockedNumber, type Contact } from "../api";
import { useApiData } from "../hooks";
import { Avatar, Empty, ErrorNote, Header, Loading, Note, SectionLabel, formatWhen } from "../ui";

/** Contacts identify counterparties across calls and conversations. */
export function ContactsPage(): JSX.Element {
  const { data, error, reload } = useApiData<Contact[]>("/api/v1/contacts");
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState<unknown>(null);

  const filtered = (data ?? []).filter(
    (contact) =>
      contact.displayName.toLowerCase().includes(search.toLowerCase()) || contact.e164.includes(search)
  );

  async function remove(contactId: string): Promise<void> {
    setActionError(null);
    try {
      await api.delete(`/api/v1/contacts/${contactId}`);
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  return (
    <>
      <Header
        title="Contacts"
        action={
          <Link className="action" to="/contacts/new" title="Add contact">
            +
          </Link>
        }
      />
      <div className="content">
        <div className="field">
          <input
            type="text"
            placeholder="Search contacts"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <ErrorNote error={error} />
        <ErrorNote error={actionError} />
        {data === null ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <Empty>No contacts.</Empty>
        ) : (
          <ul className="list">
            {filtered.map((contact) => (
              <li key={contact.id}>
                <Avatar label={contact.displayName} />
                <div className="row-main">
                  <div className="row-title">{contact.displayName}</div>
                  <div className="row-sub">{contact.e164}</div>
                  {contact.note ? <div className="row-sub">{contact.note}</div> : null}
                </div>
                <div className="row-actions">
                  <button type="button" className="danger" onClick={() => remove(contact.id)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>Contacts label counterparties in the call list, conversations and timeline.</Note>
        <Link className="btn secondary" to="/blocks">
          Blocked numbers
        </Link>
      </div>
    </>
  );
}

export function AddContactPage(): JSX.Element {
  const navigate = useNavigate();
  const [e164, setE164] = useState("+45");
  const [displayName, setDisplayName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<unknown>(null);

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await api.post("/api/v1/contacts", { e164, displayName, note: note || null });
      navigate("/contacts");
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <>
      <Header title="Add contact" back="/contacts" backLabel={"\u2190 Contacts"} />
      <div className="content">
        <form onSubmit={save}>
          <div className="field">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              value={displayName}
              placeholder="Full name"
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="e164">Phone number</label>
            <input id="e164" value={e164} onChange={(event) => setE164(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="note">Note</label>
            <input
              id="note"
              value={note}
              placeholder="Optional"
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <ErrorNote error={error} />
          <button className="btn primary" type="submit">
            Save contact
          </button>
          <Link className="btn secondary" to="/contacts">
            Cancel
          </Link>
        </form>
      </div>
    </>
  );
}

/** Blocked counterparties cannot reach any of the user's numbers. */
export function BlockListPage(): JSX.Element {
  const { data, error, reload } = useApiData<BlockedNumber[]>("/api/v1/blocks");
  const [actionError, setActionError] = useState<unknown>(null);

  async function unblock(blockId: string): Promise<void> {
    setActionError(null);
    try {
      await api.delete(`/api/v1/blocks/${blockId}`);
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  return (
    <>
      <Header
        title="Blocked"
        back="/contacts"
        backLabel={"\u2190 Contacts"}
        action={
          <Link className="action" to="/blocks/new" title="Block a number">
            +
          </Link>
        }
      />
      <div className="content">
        <ErrorNote error={error} />
        <ErrorNote error={actionError} />
        {data === null ? (
          <Loading />
        ) : data.length === 0 ? (
          <Empty>Nothing blocked.</Empty>
        ) : (
          <ul className="list">
            {data.map((blocked) => (
              <li key={blocked.id}>
                <div className="avatar sq">{"\u{1F6AB}"}</div>
                <div className="row-main">
                  <div className="row-title">{blocked.e164}</div>
                  <div className="row-sub">
                    Blocked {formatWhen(blocked.blockedAt)}
                    {blocked.reason ? ` \u00B7 ${blocked.reason}` : ""}
                  </div>
                </div>
                <div className="row-actions">
                  <button type="button" onClick={() => unblock(blocked.id)}>
                    Unblock
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>
          Blocked counterparties cannot reach any of your numbers. Their attempts are still recorded, so
          you can see what was blocked.
        </Note>
      </div>
    </>
  );
}

export function BlockNumberPage(): JSX.Element {
  const navigate = useNavigate();
  const [e164, setE164] = useState("+45");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);

  async function block(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await api.post("/api/v1/blocks", { e164, reason: reason || null });
      navigate("/blocks");
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <>
      <Header title="Block a number" back="/blocks" backLabel={"\u2190 Blocked"} />
      <div className="content">
        <form onSubmit={block}>
          <div className="field">
            <label htmlFor="blockE164">Phone number</label>
            <input id="blockE164" value={e164} onChange={(event) => setE164(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="reason">Reason</label>
            <input
              id="reason"
              value={reason}
              placeholder="Optional"
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <SectionLabel>Effect</SectionLabel>
          <Note>Calls are rejected at the provider and messages are dropped, on every number you own.</Note>
          <ErrorNote error={error} />
          <button className="btn danger" type="submit">
            Block
          </button>
          <Link className="btn secondary" to="/blocks">
            Cancel
          </Link>
        </form>
      </div>
    </>
  );
}
